import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Drawer, IconButton, InputBase, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { CalendarMonthOutlined, EventAvailableOutlined, HeadsetMic, History, Mic, OpenInNew, SwapHoriz, TodayOutlined } from "@mui/icons-material";
import { ActionChips, AreaHeader, AssistantNote, AuditNotice, ComposerFrame, ComposerIconButton, conversationSx, FocusBar, listColumnSx, HeaderBadge, HeaderLink, SendButton, SpeakerLabel, TopicList, VoiceStatus, WriterTag } from "../components/assistants/AssistantUi";
import ProposalCard from "../components/assistants/ProposalCard";
import { useDictation } from "../components/assistants/useDictation";
import { useSpeechReply } from "../components/assistants/useSpeechReply";
import { voiceService } from "../services/voiceService";
import { parseAssistant, renderMarkdown } from "../components/chat/AgentConversation";
import { useAuth } from "../contexts/AuthContext";
import { openPageInNewTab } from "../contexts/RouterContext";
import { colors } from "../theme/designSystem";
import { aiChatService, ChatMessage, ConversationSummary } from "../services/aiChatService";
import { ConversationHistory } from "../components/assistants/ConversationHistory";
import { assistantsService, MyAssistantArea, ProposalCardData } from "../services/assistantsService";

// Área Agenda dos Assistentes (item 228, F2d — RN-567): a Ani consulta; pedido de alteração vai ao agente de
// alteração, que responde logo abaixo com o cartão de proposta. Molde §6.2a; desenho aprovado no canvas
// "Super Assistente · Agenda" (28/10), com a conversa alinhada à esquerda (sem faixa vazia).

interface Props {
  area: MyAssistantArea;
  onPendingChange: (count: number) => void;
}

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { description?: string } } })?.response?.data?.description || fallback;

const AgendaAssistant: React.FC<Props> = ({ area, onPendingChange }) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery("(max-width:767.98px)");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyCard, setBusyCard] = useState<string | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Modo conversa (RN-570): ouve → envia na pausa → fala a resposta → volta a ouvir. Confirmar segue no clique.
  const [voiceReady, setVoiceReady] = useState(false);
  const [conversation, setConversation] = useState(false);
  const conversationRef = useRef(false);
  const speech = useSpeechReply(area.areaKey, setError);
  const dictation = useDictation(draft, setDraft, setError, conversation ? (text) => { void onVoice(text); } : undefined);
  const canWrite = !!area.writeAgentName;

  useEffect(() => {
    if (!area.speechReply) {
      setVoiceReady(false);
      return;
    }
    let alive = true;
    voiceService.getSettings()
      .then((v) => { if (alive) setVoiceReady(v.speechReady); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [area.speechReply]);

  const toggleConversation = () => {
    if (conversationRef.current) {
      conversationRef.current = false;
      setConversation(false);
      speech.stop();
      dictation.cancel();
      return;
    }
    speech.unlock();
    conversationRef.current = true;
    setConversation(true);
    dictation.start(draft);
  };

  // Microfone durante a fala: interrompe a voz e passa a ouvir
  const handleMic = () => {
    if (speech.speaking) {
      speech.stop();
      dictation.start("");
      return;
    }
    dictation.toggle();
  };
  const userInitial = (user?.email || "V").trim().charAt(0).toUpperCase();

  useEffect(() => {
    const pending = messages.flatMap((m) => m.proposals ?? []).filter((p) => p.status === "pending").length;
    onPendingChange(pending);
  }, [messages, onPendingChange]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, sending]);

  const send = async (text?: string) => {
    const message = (text ?? draft).trim();
    if (!message || sending) return;
    dictation.cancel();
    speech.stop();
    setSending(true);
    setError(null);
    setDraft("");
    setMessages((ms) => [...ms, { role: "user", content: message, createdAt: new Date().toISOString() }]);
    let spoken = "";
    try {
      const res = await aiChatService.send(area.readAgentId, conversationId, message, "Super Assistente · Agenda", null, area.areaKey);
      setConversationId(res.conversationId);
      spoken = [parseAssistant(res.reply).text, res.handoff ? parseAssistant(res.handoff.content).text : ""].filter(Boolean).join("\n");
      setMessages((ms) => [
        ...ms,
        // A Ani é a única voz; os cartões do agente de alteração ficam embaixo da fala dela
        ...(res.reply
          ? [{ role: "assistant", content: res.reply, createdAt: new Date().toISOString(), agentId: area.readAgentId, agentName: area.readAgentName,
              proposals: res.proposals ?? null }]
          : []),
        ...(res.handoff
          ? [{ role: "assistant", content: res.handoff.content, createdAt: new Date().toISOString(), agentId: res.handoff.agentId,
              agentName: res.handoff.agentName ?? area.writeAgentName, proposals: res.handoff.proposals }]
          : []),
      ]);
    } catch (e) {
      setMessages((ms) => ms.slice(0, -1));
      setDraft(message);
      setError(errorText(e, "A IA não respondeu. Tente de novo."));
    } finally {
      setSending(false);
    }
    if (!conversationRef.current) return;
    if (spoken) await speech.speakBrief(spoken);
    // Se a pessoa já reabriu o microfone durante a fala, start não faz nada
    if (conversationRef.current) dictation.start("");
  };

  // Fala no modo conversa: "sim"/"não" respondem ao "Quer que eu leia o restante?"; o resto é pedido novo
  const onVoice = async (text: string) => {
    const answer = await speech.answerFollowUp(text);
    if (answer === null) {
      await send(text);
      return;
    }
    setDraft("");
    if (conversationRef.current) dictation.start("");
  };

  // O estado do cartão vem sempre do servidor: depois de qualquer decisão (certa ou recusada) a conversa é relida
  const refresh = useCallback(async () => {
    if (!conversationId) return;
    try {
      setMessages(await aiChatService.messages(conversationId));
    } catch {
      // Mantém o que está na tela; a próxima ação relê de novo
    }
  }, [conversationId]);

  const decide = async (card: ProposalCardData, action: "confirm" | "discard" | "undo") => {
    setBusyCard(card.id);
    setCardErrors(({ [card.id]: _, ...rest }) => rest);
    try {
      await assistantsService[action](card.id);
    } catch (e) {
      setCardErrors((m) => ({ ...m, [card.id]: errorText(e, "Não foi possível concluir. Tente de novo.") }));
    } finally {
      await refresh();
      setBusyCard(null);
    }
  };

  // Histórico (RN-573): abrir uma conversa da lista ou começar uma nova, sem perder a voz no meio
  const [historyOpen, setHistoryOpen] = useState(false);
  const openConversation = async (c: ConversationSummary) => {
    speech.stop();
    dictation.cancel();
    setHistoryOpen(false);
    setError(null);
    setDraft("");
    setConversationId(c.id);
    try {
      setMessages(await aiChatService.messages(c.id));
    } catch {
      setMessages([]);
      setError("Não foi possível abrir essa conversa.");
    }
  };
  const newConversation = () => {
    speech.stop();
    dictation.cancel();
    setHistoryOpen(false);
    setError(null);
    setConversationId(null);
    setMessages([]);
  };
  const history = (
    <ConversationHistory agentId={area.readAgentId} area={area.areaKey} activeId={conversationId}
      reloadKey={`${conversationId}:${messages.length}`}
      onOpen={openConversation} onNew={newConversation} />
  );

  const openAgenda = (date: string | undefined, patientId: string | undefined) => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    if (patientId) params.patientId = patientId;
    openPageInNewTab("schedule", params);
  };

  const prefill = (text: string) => {
    setDraft(text);
    window.setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(text.length, text.length);
      }
    }, 0);
  };

  const topics = [
    { title: "Minha agenda de hoje", does: "quem vem, horários e confirmações", icon: <TodayOutlined />, onClick: () => send("Como está a minha agenda de hoje?") },
    { title: "Horários livres", does: "para reposição ou encaixe", icon: <EventAvailableOutlined />, onClick: () => prefill("Horários livres de ") },
    ...(canWrite
      ? [{ title: "Remarcar ou cancelar uma sessão", does: "o assistente prepara, você confirma", icon: <SwapHoriz />, onClick: () => prefill("Remarcar a sessão de ") }]
      : []),
  ];

  const meta = canWrite ? `${area.readAgentName} consulta · ${area.writeAgentName} prepara alterações` : `${area.readAgentName} consulta · só consulta`;

  const header = isMobile ? (
    <FocusBar
      avatar={<CalendarMonthOutlined sx={{ fontSize: 22 }} aria-hidden />}
      title="Agenda da clínica"
      meta={canWrite ? `${area.readAgentName} consulta · alterações com confirmação` : meta}
      actions={
        <>
          <Tooltip title="Conversas">
            <IconButton onClick={() => setHistoryOpen(true)} aria-label="Conversas" sx={{ width: 44, height: 44, color: colors.textPrimary }}><History /></IconButton>
          </Tooltip>
          <Tooltip title="Abrir a Agenda">
            <IconButton onClick={() => openPageInNewTab("schedule")} aria-label="Abrir a Agenda" sx={{ width: 44, height: 44, color: colors.textPrimary }}><OpenInNew /></IconButton>
          </Tooltip>
        </>
      }
    />
  ) : (
    <AreaHeader
      avatar={<CalendarMonthOutlined aria-hidden />}
      title="Agenda da clínica"
      meta={<>{meta} · <HeaderLink onClick={() => openPageInNewTab("schedule")}>Abrir a Agenda</HeaderLink></>}
      badge={canWrite ? <HeaderBadge>Nada é gravado sem a sua confirmação</HeaderBadge> : undefined}
    />
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: colors.white }}>
      {header}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
      {isMobile ? (
        <Drawer anchor="left" open={historyOpen} onClose={() => setHistoryOpen(false)} disablePortal PaperProps={{ sx: { width: "85vw", maxWidth: 340 } }}>
          {history}
        </Drawer>
      ) : (
        <Box component="aside" aria-label="Conversas da Agenda" sx={listColumnSx}>
          {history}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AuditNotice clinicName={user?.clinicName} />
      <Box ref={scrollRef} sx={{ flexGrow: 1, overflow: "auto", px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
        <Box role="log" aria-live="polite" aria-relevant="additions" aria-label="Conversa sobre a agenda"
          sx={{ display: "flex", flexDirection: "column", gap: 2.5, ...conversationSx }}>
          {messages.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 600, color: colors.textStrong }}>O que você quer fazer na agenda?</Typography>
              <TopicList topics={topics} isMobile={isMobile} disabled={sending} />
            </Box>
          )}
          {messages.map((m, i) => {
            const isUser = m.role === "user";
            const isWriter = !isUser && !!m.agentId && m.agentId !== area.readAgentId;
            const { text, actions } = isUser ? { text: m.content, actions: [] as string[] } : parseAssistant(m.content);
            return (
              <Box key={i}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SpeakerLabel isUser={isUser} initial={userInitial} name={isUser ? "Você" : m.agentName || area.readAgentName} />
                  {isWriter && <WriterTag />}
                </Box>
                <Box sx={{ fontSize: "0.9375rem", lineHeight: 1.55, color: colors.textStrong, wordBreak: "break-word", whiteSpace: isUser ? "pre-wrap" : "normal" }}>
                  {isUser ? <Box className="c4-text">{text}</Box> : renderMarkdown(text)}
                </Box>
                {i === messages.length - 1 && <ActionChips actions={actions} onPick={(a) => send(a)} isMobile={isMobile} disabled={sending} />}
                {(m.proposals ?? []).length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
                    {(m.proposals ?? []).map((card) => (
                      <ProposalCard key={card.id} card={card} isMobile={isMobile} busy={busyCard === card.id} error={cardErrors[card.id]}
                        onConfirm={() => decide(card, "confirm")} onDiscard={() => decide(card, "discard")} onUndo={() => decide(card, "undo")}
                        onOpenAgenda={openAgenda} />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
          {sending && (
            <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary, fontSize: "0.8125rem" }}>
              <CircularProgress size={14} aria-hidden /> consultando a agenda…
            </Box>
          )}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, px: { xs: 1.5, md: 4 }, pt: 1, pb: { xs: 1, md: 1.75 }, borderTop: isMobile ? `1px solid ${colors.backgroundAlt}` : "none" }}>
        <Box>
          <ComposerFrame isMobile={isMobile}>
            <InputBase fullWidth multiline minRows={isMobile ? 1 : 2} maxRows={6} value={draft} inputRef={inputRef}
              placeholder={canWrite ? "Pergunte sobre a agenda ou peça uma alteração…" : "Pergunte sobre a agenda…"}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              inputProps={{ "aria-label": "Mensagem para a Agenda", maxLength: 4000 }}
              sx={{ fontSize: "0.9375rem", lineHeight: 1.45, color: colors.textStrong, py: 0.5, pl: 1, "& textarea::placeholder": { color: colors.textSecondary, opacity: 1 } }} />
            {voiceReady && dictation.supported && (
              <ComposerIconButton label={conversation ? "Sair do modo conversa" : "Modo conversa"} title={conversation ? "Sair do modo conversa" : "Modo conversa: fale e ouça a resposta"}
                onClick={toggleConversation} isMobile={isMobile} pressed={conversation} tone="voice">
                <HeadsetMic />
              </ComposerIconButton>
            )}
            <ComposerIconButton label={speech.speaking ? "Interromper e falar" : dictation.listening ? "Parar o ditado" : "Ditar"}
              onClick={handleMic} isMobile={isMobile} pressed={dictation.listening} tone="recording">
              <Mic />
            </ComposerIconButton>
            <SendButton onClick={() => send()} disabled={sending || !draft.trim()} isMobile={isMobile} />
          </ComposerFrame>
          {conversation && (
            <VoiceStatus speaking={speech.speaking} sending={sending} transcribing={dictation.transcribing} listening={dictation.listening} />
          )}
          <Box sx={{ textAlign: "left", "& p": { textAlign: "left" } }}>
            <AssistantNote>
              {canWrite
                ? isMobile ? "Nada é gravado sem a sua confirmação." : "A Ani consulta. Alterações só de sessões avulsas, e só depois do seu Confirmar."
                : "A Ani só consulta. Alterações são feitas na tela da Agenda."}
            </AssistantNote>
          </Box>
        </Box>
      </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default AgendaAssistant;
